import MathTypeInternal, { IntegrationModel } from "@wiris/mathtype-ckeditor5";
import MathML from "@wiris/mathtype-html-integration-devkit/src/mathml";
import { Editor, ViewElement, toWidget } from "ckeditor5";

export default class MathType extends MathTypeInternal {
  declare integration: IntegrationModel;

  override _addIntegration() {
    return (this.integration = super._addIntegration());
  }

  override init() {
    super.init();

    unMonkeyPatchWiris(this.editor);

    monkeyPatchEditorCommand(this.editor, "MathType");
    monkeyPatchEditorCommand(this.editor, "ChemType");

    this.editor.conversion.for("editingDowncast").elementToElement({
      converterPriority: "highest",
      model: "mathml",
      view: (modelElement, { writer, consumable }) => {
        consumable.consume(modelElement, "insert");
        const MathJax = window["MathJax"];

        if (!MathJax) {
          return;
        }

        const formula = (modelElement.getAttribute("formula") ?? "") as string;

        // this seems more reliable than just passing display=true
        const converted = formula.replace(/<math/, `<math display="block"`);

        const mjxSvg: Element = MathJax.mathml2svg(converted, {
          display: true,
        });

        const mjxContainer = writer.createRawElement(
          "span",
          {},
          (domElement) => {
            domElement.innerHTML = mjxSvg.outerHTML;

            domElement.classList.add("Wirisformula");

            domElement.setAttribute(
              "data-mathml",
              MathML.safeXmlEncode(
                modelElement.getAttribute("formula") as string,
              ),
            );

            domElement.addEventListener("click", (e) => {
              // double click only
              if (e.detail !== 2) return;

              this.integration.core.editionProperties.temporalImage =
                domElement;
              this.integration.core.editionProperties.isNewElement = true;
              this.integration.openExistingFormulaEditor();
            });
          },
        );

        const container = writer.createContainerElement("span", {}, [
          mjxContainer,
        ]);
        return toWidget(container, writer);
      },
    });
  }

  override destroy(): void {
    // work around https://github.com/wiris/html-integrations/issues/1047
    this.integration.destroy();
  }
}

const monkeyPatchEditorCommand = (editor: Editor, name: string) => {
  const command = editor.commands.get(name);
  if (command == null) {
    console.warn(`Editor does not have command '${name}'`);
    return;
  }

  if (!("_getSelectedImage" in command)) {
    console.warn(`Command '${name}' does not appear to be a MathType command`);
    return;
  }

  command._getSelectedImage = () => {
    const selection = editor.editing.view.document.selection;
    if (selection.isCollapsed || selection.rangeCount !== 1) return;

    const element = Array.from(
      selection.getSelectedElement()?.getChildren() ?? [],
    ).find((e): e is ViewElement => e.is("element"));

    if (element == null) return;

    const domElement = editor.editing.view.domConverter.mapViewToDom(element);
    if (domElement == null) return;

    return domElement;
  };
};

const unMonkeyPatchWiris = (editor: Editor) => {
  //
  // The wiris plugin monkey-patches the editor DataController get and set methods
  // to supposedly handle inline latex in the form $$latex$$
  //
  // We can get rid of this fix the wiris plugin merges our fix
  // https://github.com/wiris/html-integrations/pull/886
  //
  //
  // We don't care about that, and their patches cause a couple different problems:
  //  - discarding the second option to data.set
  //  - causing excessive network requests on data.get
  //
  // So let's undo their garbage, put the methods back to their original decorated versions
  // see https://github.com/examspark/ckeditor5/blob/master/packages/ckeditor5-utils/src/observablemixin.ts#L280-L282
  //
  editor.data.set = (...args: any[]) => editor.data.fire("set", args) as any;
  editor.data.get = (...args: any[]) => editor.data.fire("get", args) as any;
};

export type MathTypeParameters = {
  // https://docs.wiris.com/mathtype/en/mathtype-integrations/technical-configurations/parameters.html
  editorParameters?: {
    fonts: Array<{
      id: string;
      label?: string;
      fontFamily?: string;
    }>;
    fontFamily?: string;
    fontSize?: string;
    detectHand?: boolean;
    toolbar?: string;
  };
};

declare module "ckeditor5" {
  interface EditorConfig {
    mathTypeParameters?: MathTypeParameters;
  }
}
